// HeatmapStats.js — at-a-glance numbers for the current view (matches,
// distinct scorelines, and, for a single season, all-time scorigamis that
// debuted in it). Rendered as tiles in the side panel.

import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { BRAND } from "../../theme";
import { EYEBROW_SX } from "./HeatmapLegend";
import { ALL_LABEL, HOME_AWAY, seasonStartYear } from "./utils";

function Tile({ value, label, tip }) {
  return (
    <Tooltip title={tip} placement="top" arrow>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          px: 1.5,
          py: 1.25,
          borderRadius: 2,
          background: "rgba(255,255,255,0.035)",
          border: "1px solid rgba(255,255,255,0.07)",
          cursor: "help",
        }}
      >
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 900,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
            fontFeatureSettings: '"tnum"',
            background: BRAND.gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {value}
        </Typography>
        <Typography sx={{ ...EYEBROW_SX, fontSize: 9.5, mt: 0.6 }}>{label}</Typography>
      </Box>
    </Tooltip>
  );
}

export default function HeatmapStats({ scoreSummaryMap, scoreMap, season, axes }) {
  const isAllSeasons = !season || season === ALL_LABEL;

  const stats = useMemo(() => {
    const summary = Object.values(scoreSummaryMap || {});
    const scorelinesTip =
      axes === HOME_AWAY
        ? "Different home–away scorelines (3–1 and 1–3 count separately)"
        : "Different final scores";
    if (isAllSeasons) {
      if (!summary.length) return [];
      return [
        {
          value: summary.reduce((acc, s) => acc + Number(s.count || 0), 0).toLocaleString(),
          label: "Matches",
          tip: "Premier League matches since 1992",
        },
        { value: summary.length.toLocaleString(), label: "Scorelines", tip: `${scorelinesTip} ever recorded` },
      ];
    }
    const entries = Object.entries(scoreMap || {});
    if (!entries.length) return [];
    const out = [
      {
        value: entries.reduce((acc, [, list]) => acc + (list?.length || 0), 0).toLocaleString(),
        label: "Matches",
        tip: `Matches played in ${season}`,
      },
      { value: entries.length.toLocaleString(), label: "Scorelines", tip: `${scorelinesTip} in ${season}` },
    ];
    const seasonStart = parseInt(season.slice(0, 4), 10);
    const debuts = summary.filter(
      (s) => s.count === 1 && s.firstOccurrence?.utcDate && seasonStartYear(s.firstOccurrence.utcDate) === seasonStart
    ).length;
    if (debuts > 0) {
      out.push({ value: debuts.toLocaleString(), label: "New to history", tip: `All-time scorigamis that debuted in ${season}` });
    }
    return out;
  }, [isAllSeasons, scoreSummaryMap, scoreMap, season, axes]);

  if (!stats.length) return null;
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      {stats.map((s) => (
        <Tile key={s.label} {...s} />
      ))}
    </Box>
  );
}
